// Create, edit, approve, view, or otherwise interact with an Order
import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormControlLabel, FormGroup, InputAdornment, Link, MenuItem, Select, Stack, Switch, TextField, Typography, useTheme } from "@mui/material";
import APIRequest, { APIError, BuildAPIURL } from "system/APIRequest";
import { AccountCircle, AddLink, AddShoppingCart, Business, Close, ConnectWithoutContact, Description, EventRepeat, Handshake, Preview, RequestPage } from "@mui/icons-material";
import TopBar from "components/dashboardParts/TopBar";
import Markdown from "react-markdown";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useParams } from "react-router";
import { isEqual, cloneDeep } from 'lodash';
import { InfegyAccount, permissionTest } from "system/InfegyUser";
import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonMenu from "./ButtonMenu";

// Used for pretty printing relative date strings
dayjs.extend(relativeTime);

// Utils
function formatCurrency(amt: number, currency?: string): string {
  const v = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency?.toUpperCase() || "USD",
  }).format(amt);
  return v.endsWith(".00") ? v.slice(0, -3) : v;
}

interface HubspotContact {
  readonly hubspot_id: string;
  readonly email: string;
  readonly name: string;
}

// Structure that holds info on a Hubspot Deal and Company, retrieved from Starscape
interface HubspotDealInfo {
  readonly deal: {
    readonly hubspot_id: string;
    readonly name: string;
    readonly created_at: string;
    readonly closes_at: string;
    readonly amount: number;
    readonly link: string;
  };
  readonly company: {
    readonly hubspot_id: string;
    readonly name: string;
    readonly domain: string;
    readonly country: string;
    readonly link: string;
  };
  readonly contacts: Array<HubspotContact>;
}

// Info for the Starscape Account we may create
interface AccountInfo {
  id?: string;
  name?: string;
  domain?: string;
  hubspot_id?: string;
}

// Simplified Starscape user, used for assigning roles to the deal
interface SimpleUser {
  id: string;
  email: string;
  name: string;
  _deleted?: boolean;
}

// Used in BillingPlans, API pricing, and products, to specify populated default values for price or allotment
interface BillingOrderDefaults {
  price?: number;
  allotment?: number;
}

// A plan, which can be used in a subscription
interface BillingPlan {
  id: string;
  name: string;
  description: string;
  requires_social_plan?: boolean;
  order_defaults: BillingOrderDefaults;
  roles: {
    [key: string]: {
      order_defaults?: BillingOrderDefaults;
    };
  };
  // Optional pricing fields for order form
  base_price?: number;
  discount_type?: "percent" | "currency";
  discount_amount?: number;
  _always_editable?: boolean;
}

// A product, a one-time purchase
interface BillingProduct {
  name: string;
  description: string;
  order_defaults?: BillingOrderDefaults;
  _always_editable?: boolean;
}

// API pricing, which can be pre-alloted, and/or pay-as-you-go
interface BillingAPIPrice {
  id: string;
  name: string;
  description: string;
  per: string;
  price: number;
}

// The full set of plans and products available
interface BillingProducts {
  account_plans: Array<BillingPlan>;
  social_plans: Array<BillingPlan>;
  addon_plans: Array<BillingPlan>;
  api_pricing: {
    [key: string]: BillingAPIPrice;
  };
  products: Array<BillingProduct>;

  // List of known roles, here for name lookup purposes
  roles: {
    [key: string]: {
      name: string;
    };
  };
  
  _plansById: {
    [key: string]: BillingPlan;
  };
}

interface OrderPlan {
  id: string;
  name: string;
  description: string;
  base_price: number;
  discount_type: "percent" | "currency";
  discount_amount: number;
  _always_editable?: boolean;
  roles: {
    [key: string]: {
      order?: BillingOrderDefaults;
    };
  };
}

interface OrderAPIPrice {
  name: string;
  description: string;
  per: string;
  price: number;
  allotment: {
    count: number;
    price: number;
  };
}

interface OrderProduct {
  name: string;
  description: string;
  quantity: number;
  base_price: number;
  discount_type: "percent" | "currency";
  discount_amount: number;
  _always_editable?: boolean; // not used; here so Typescript doesn't complain
}

interface OrderSigningData {
  bill_to_name?: string;
  po_number?: string;
  billing_address?: string;
  signed_by?: string;
  sign_token?: string;
  billing_emails?: string;
  api_notification_emails?: string;
  details?: string;
}

interface OrderChange {
  order_id: string;
  type: string;
}

interface OrderProducts {
  currency: 'usd' | 'eur';
  
  account_plan?: OrderPlan;
  social_plan?: OrderPlan;
  addon_plans?: Array<OrderPlan>;
  api_pricing: {
    [key: string]: OrderAPIPrice;
  };
  products?: Array<OrderProduct>;

  subscription_start_date?: string; // ISO-8601
  subscription_term?: string; // ISO period (like P1M)
  invoice_frequency?: string; // ISO period (like P1M)
  invoice_start_date?: string; // ISO-8601
  invoice_net?: number; // days to pay, defaulting to 30
  auto_renew?: boolean;

  _trial?: boolean;
  trial_duration?: number;
  
  account_info?: AccountInfo;
  hubspot_deal_id?: string;
  
  account_id?: string; // existing account ID
  
  admin_email?: string; // Where to send the initial admin invite
  admin_role: string;  // The role to assign to the admin user
  
  _send_order_by_email: boolean; // If true, we will send a link to this order via email to...
  order_email?: string;
  
  reps?: {
    lead_source?: Array<SimpleUser>;
    lead_chase?: Array<SimpleUser>;
    account_exec?: Array<SimpleUser>;
    solutions_consultant?: Array<SimpleUser>;
    client_success?: Array<SimpleUser>;
  }
  
  order_form_note?: string;
  invoice_memo?: string;
  internal_notes?: string;
  
  change?: OrderChange;
  
  // API can send these, we won't populate them
  _approved_at?: string;
  _sent_at?: string;
  _signed_at?: string;
  _bill_to_name?: string;
  _signing_data?: OrderSigningData;
  
  // End of the current term, set if we loaded an existing order
  _end_of_current_term?: string;
}

interface EditableInvoiceFields {
  bill_to_name: string;
  bill_to_emails: string;
  api_notification_emails: string;
  po_number: string;
  billing_address: string;
  memo: string;
}

// Renders HubspotDealInfo into an MUI Card
interface HubspotDealInfoCardProps {
  hubspotDealInfo: HubspotDealInfo;
}
function HubspotDealInfoCard(props: HubspotDealInfoCardProps) {
  return (
    <>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          mt: 1
        }}>
        <Link href={props.hubspotDealInfo.deal.link} target="_blank" rel="noreferrer">
          <Avatar>
            <Handshake />
          </Avatar>
        </Link>
        <Stack sx={{
          ml: 1
        }}>
          <Link variant="h4" href={props.hubspotDealInfo.deal.link} target="_blank" rel="noreferrer">
            {props.hubspotDealInfo.deal.name}
          </Link>
          <Typography>{formatCurrency(props.hubspotDealInfo.deal.amount)} / {props.hubspotDealInfo.contacts.length} contacts</Typography>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          mt: 2
        }}>
        <Link href={props.hubspotDealInfo.company.link} target="_blank" rel="noreferrer">
          <Avatar>
            <Business />
          </Avatar>
        </Link>

        <Stack sx={{
          ml: 1
        }}>
          <Link variant="h4" href={props.hubspotDealInfo.company.link} target="_blank" rel="noreferrer">
            {props.hubspotDealInfo.company.name}
          </Link>
          <Typography>
            {props.hubspotDealInfo.company.domain} / {props.hubspotDealInfo.company.country}
          </Typography>
        </Stack>
      </Stack>
    </>
  );
}

interface RepSelectionProps {
  users: Array<SimpleUser>;
  label: string;
  onChange: (users: Array<SimpleUser> | undefined) => void;
  value?: Array<SimpleUser> | null;
  disabled?: boolean;
}
function RepSelection(props: RepSelectionProps) {
  return (
    <Autocomplete openOnFocus autoComplete autoHighlight multiple disabled={props.disabled}
      options={props.users}
      value={props.value || []}
      getOptionLabel={(option: SimpleUser) =>
        `${option.name} (${option.email})`
      }
      isOptionEqualToValue={(option: SimpleUser, value: SimpleUser) =>
        option.id === value.id
      }
      renderInput={(params) => (
        <TextField variant="standard"
          label={props.label}
          {...params}
        />
      )}
      onChange={(_event: React.SyntheticEvent, value: SimpleUser[]) => {
        if(!value || value.length === 0) {
          props.onChange(undefined);
        } else props.onChange(value);
      }}
      renderValue={(value, getItemProps) =>
        value.map((option, index) => (
          <Chip
            label={option.name}
            icon={<AccountCircle />}
            color="info"
            {...getItemProps({ index })}
            key={`c-${props.label}-${index}`}
          />
        ))
      }
    />
  );
}


interface PlanOrderEditorProps {
  plan: OrderPlan | OrderProduct;
  billingPlan?: BillingPlan | BillingProduct;
  isProduct?: boolean;
  onChange?: (plan: OrderPlan | OrderProduct) => void;
  onDelete?: () => void;
  currency: 'usd'|'eur';
  disabled?: boolean;
  allowEditing?: boolean;
}
function PlanOrderEditor(props: PlanOrderEditorProps) {
  const finalPrice =
    Math.max(
      props.plan.discount_type === "percent"
        ? props.plan.base_price * (1 - props.plan.discount_amount / 100)
        : props.plan.base_price - props.plan.discount_amount,
      0,
    ) * ("quantity" in props.plan ? props.plan.quantity : 1);
  
  const currencySymbol = props.currency === 'eur' ? '€' : '$';
  
  const isEditable = (props.isProduct || props.allowEditing || props.plan?._always_editable === true || permissionTest("super_admin")) && props.disabled !== true;

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: "space-between",
        alignItems: "flex-start",
        mb: 2
      }}>
      <Box sx={{
        flex: 1
      }}>
        <Stack
          direction="row"
          sx={{
            alignItems: "start",
            gap: 1
          }}>
          {!props.disabled &&
            <Button
              size="small"
              sx={{ minWidth: "auto", p: 0 }}
              onClick={() => {
                if (props.onDelete) props.onDelete();
              }}
            >
              <Close fontSize="small" color="error" />
            </Button>
          }
          <Box sx={{
            width: "100%"
          }}>
            {/* Product names and descriptions are editable, unlike subscription items */}
            {isEditable && (
              <>
                <TextField required disabled={props.disabled}
                  variant="standard"
                  fullWidth={true}
                  value={"name" in props.plan ? props.plan.name : ""}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    if (props.onChange) {
                      props.onChange({ ...props.plan, name: e.target.value });
                    }
                  }}
                />
                <TextField required multiline fullWidth disabled={props.disabled}
                  sx={{ marginTop: 1 }}
                  value={
                    "description" in props.plan ? props.plan.description : ""
                  }
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    if (props.onChange) {
                      props.onChange({
                        ...props.plan,
                        description: e.target.value,
                      });
                    }
                  }}
                />
              </>
            )}
            {!isEditable && (
              <>
                <Typography variant="h4">
                  {"name" in props.plan
                    ? props.plan.name
                    : props.billingPlan?.name}
                </Typography>
                <Markdown>
                  {"description" in props.plan
                    ? props.plan.description
                    : props.billingPlan?.description}
                </Markdown>
              </>
            )}
          </Box>
        </Stack>
      </Box>
      <Box
        sx={{
          flex: 1,
          pl: 3
        }}>
        <Stack sx={{
          gap: 1
        }}>
          {/* First row: Base Price, Discount Type + Amount */}
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: "center"
            }}>
            {props.isProduct && (
              <TextField disabled={props.disabled}
                label="Quantity"
                type="number"
                size="small"
                value={"quantity" in props.plan ? props.plan.quantity : 0}
                onChange={(e) => {
                  if (props.onChange) {
                    props.onChange({
                      ...props.plan,
                      quantity: parseInt(e.target.value) || 0,
                    });
                  }
                }}
                sx={{ width: 70 }}
              />
            )}

            <TextField disabled={props.disabled}
              label="Base Price"
              type="number"
              size="small"
              value={props.plan.base_price || 0}
              onChange={(e) => {
                if (props.onChange) {
                  props.onChange({
                    ...props.plan,
                    base_price: parseFloat(e.target.value) || 0,
                  });
                }
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">{currencySymbol}</InputAdornment>
                  ),
                },
              }}
              sx={{ width: 120 }}
            />

            <TextField disabled={props.disabled}
              label="Discount"
              type="number"
              size="small"
              value={props.plan.discount_amount}
              onChange={(e) => {
                if (props.onChange) {
                  props.onChange({
                    ...props.plan,
                    discount_amount: parseFloat(e.target.value) || 0,
                  });
                }
              }}
              sx={{ width: 100 }}
            />
            <FormControl size="small" sx={{ minWidth: 60 }}>
              <Select disabled={props.disabled}
                value={props.plan.discount_type}
                onChange={(e) => {
                  if (props.onChange) {
                    props.onChange({
                      ...props.plan,
                      discount_type: e.target.value as "percent" | "currency",
                    });
                  }
                }}
              >
                <MenuItem value="percent">%</MenuItem>
                <MenuItem value="currency">{currencySymbol}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          {/* Second row: Final Price (read-only, greyed out) */}
          <TextField disabled variant="standard"
            value={ formatCurrency(finalPrice, props.currency) + (props.isProduct ? "" : " / year") }
          />
        </Stack>
      </Box>
    </Stack>
  );
}

export default function OrderFormPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { orderId, accountId } = useParams() as { orderId?: string, accountId?: string };
  const [searchParams, ] = useSearchParams();
  
  const [initError, setInitError] = useState<string | undefined>(undefined);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [infegyUsers, setInfegyUsers] = useState<Array<SimpleUser>>([]);
  const [billingProducts, setBillingProducts] = useState<BillingProducts | undefined>(undefined);
  const [hubspotInfoLoading, setHubspotInfoLoading] = useState<boolean>(false);
  const [hubspotInfoError, setHubspotInfoError] = useState<string | undefined>(undefined);
  const [hubspotDealInfo, setHubspotDealInfo] = useState<HubspotDealInfo | undefined>(undefined);
  const [orderProducts, setOrderProducts] = useState<OrderProducts>({ currency: 'usd', api_pricing: {}, admin_role: 'view-only', _send_order_by_email: true });
  const [sourceOrder, setSourceOrder] = useState<OrderProducts | undefined>(undefined); // Set if we started as a copy of another order
  const [saveState, setSaveState] = useState<'saving' | 'saved' | undefined>(undefined);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [originalOrder, setOriginalOrder] = useState<OrderProducts | undefined>(undefined); // used to see if changes have been made
  const [signDialogOpen, setSignDialogOpen] = useState<boolean>(false);
  const [signingInProgress, setSigningInProgress] = useState<boolean>(false);
  const [signingError, setSigningError] = useState<string | undefined>(undefined);
  const [editableInvoiceFields, setEditableInvoiceFields] = useState<EditableInvoiceFields>();
  const [accountInfo, setAccountInfo] = useState<InfegyAccount | undefined>(undefined);
  const [changeType, setChangeType] = useState<string>(accountId ? searchParams.get('type') || '' : '');
  
  // Test for editability
  // No one can edit an order once its been approved
  const isSuperAdmin = permissionTest("super_admin");
  const isReadOnly = !(!orderProducts._approved_at);
  const termsReadOnly = isReadOnly || changeType === 'change'; // blocks modifying terms
  
  // Save the symbol used for the currently-set currency
  const currencySymbol = orderProducts.currency === 'eur' ? '€' : '$';
  
  // Generate the current state of editable invoice fields from an order
  const getEditableInvoiceFieldsFromOrder = (_order: OrderProducts) => {
    const fields: EditableInvoiceFields = {
      bill_to_name: _order._bill_to_name || '',
      bill_to_emails: _order._signing_data?.billing_emails || '',
      api_notification_emails: _order._signing_data?.api_notification_emails || '',
      po_number: _order._signing_data?.po_number || '',
      billing_address: _order._signing_data?.billing_address || '',
      memo: _order.invoice_memo || ''
    };
    
    return fields;
  };
  
  // Load the order's data from the API
  const loadOrder = (_orderId: string|undefined) => {
    APIRequest(`billing/order/${_orderId}`, {}, {}, true).then((_orderProducts: OrderProducts) => {
      if(!changeType) {
        handleHubspotDealChange(_orderProducts.hubspot_deal_id);
      } else {
        _orderProducts.hubspot_deal_id = undefined;
        
        if(changeType !== 'trial') {
          _orderProducts._trial = undefined;
          _orderProducts.trial_duration = undefined;
        }
      }
      
      _orderProducts._send_order_by_email = !!_orderProducts.order_email;
      
      let localChange = changeType;
      if (_orderProducts.change) {
        if (!changeType) {
          // If we are loading a change order, we need this
          setChangeType(_orderProducts.change.type);
        }
        localChange = _orderProducts.change.type;
      }
      
      if(accountId) {
        // If we are loading using an account ID, we act like a "create from copy" operation, so reset state tracking vars
        setSourceOrder(cloneDeep(_orderProducts)); // save the original source
        if(_orderProducts.account_plan) {
          _orderProducts.subscription_start_date = dayjs(new Date()).toISOString();
        } else {
          _orderProducts.subscription_start_date = undefined;
        }
        
        if(localChange) {
          _orderProducts.products = undefined;
          if(localChange === 'trial') {
            // Trials cannot have products
            _orderProducts.trial_duration = 7;
            _orderProducts._trial = true;
          } else if(localChange === 'renewal') {
            _orderProducts.subscription_start_date = _orderProducts._end_of_current_term;
          }
        }
        
        // Clear any other state information on the order
        _orderProducts.invoice_start_date = undefined;
        _orderProducts._approved_at = undefined;
        _orderProducts._sent_at = undefined;
        _orderProducts._signed_at = undefined;
        _orderProducts._signing_data = undefined;
      } else {
        setOriginalOrder(cloneDeep(_orderProducts));
      }
      
      // We need these to be distinct references, so we don't modify both
      setOrderProducts(_orderProducts);
      
      setEditableInvoiceFields(getEditableInvoiceFieldsFromOrder(_orderProducts));
      setIsInitializing(false);
    }).catch((reason: APIError) => {
      console.error("Error loading order", reason);
      setInitError(`Error loading order by ID ${_orderId}`);
    });
  };

  useEffect(() => {
    // Load all possibly-relevant Infegy users
    const userQuery = JSON.stringify([
      { id: "_account", op: "=", value: "Infegy" },
      { id: "email", op: "ends", value: "@infegy.com" },
      { id: "email", op: "not-contain", value: "+" }
    ]);
    APIRequest("obj/user", { per_page: "1000", minimal: "1", admin: "1", q: userQuery}).then((response: Response) => {
      setInfegyUsers(
        (response as unknown as { output: SimpleUser[] }).output.map(({ id, email, name, _deleted }) => ({ id, email, name, _deleted })));
    }).catch((reason: APIError) => {
      console.error("Error loading users", reason);
      setInitError("Error loading users");
    });
    
    if(accountId) {
      APIRequest(`obj/account/${accountId}`, { admin: "1", include_deleted: "1" }).then((response: Response) => {
        const _accountInfo = (response as unknown as { output: InfegyAccount }).output;
        setAccountInfo(_accountInfo);
        
        const _orderId = _accountInfo.billing_order_id ? _accountInfo.billing_order_id : _accountInfo.trial_order_id;
        if(_orderId) {
          // Load existing order on the account as a basis, like a "create from copy"
          loadOrder(_orderId);
        }
        
      }).catch((reason: APIError) => {
        console.error("Error loading account info", reason);
        setInitError("Error loading account info");
      });
    }

    APIRequest("billing/products").then((response: BillingProducts) => {
      // Set up a map so we can easily look up info on plans from the order info
      response._plansById = {};
      response.account_plans.map((plan: BillingPlan) => response._plansById[plan.id] = plan);
      response.social_plans.map((plan: BillingPlan) => response._plansById[plan.id] = plan);
      response.addon_plans.map((plan: BillingPlan) => response._plansById[plan.id] = plan);
      setBillingProducts(response);
      
      if(orderId) {
        loadOrder(orderId);
      } else {
        setIsInitializing(false);
        setOrderProducts({...orderProducts, 
          api_pricing: Object.fromEntries(Object.entries(response.api_pricing).map(([key, pricing]) => [key, {
            name: pricing.name,
            description: pricing.description,
            per: pricing.per,
            price: pricing.price,
            allotment: {
              count: 0,
              price: 0
            }
          }]))
        })
      }
    }).catch((reason: APIError) => {
      console.error("Error loading billing/products:", reason);
      setInitError("Error loading billing/products");
    });
  }, []);
  
  // Little utility to make links based on API urls
  const makeLink = (endpoint: string) => {
    return BuildAPIURL(endpoint).replace('/api/', '/');
  }

  const handleHubspotDealChange = (value?: string) => {
    if (hubspotInfoLoading) return;

    value = value || "";

    if (value.length < 6) return;

    setHubspotInfoLoading(true);
    setHubspotInfoError(undefined);

    APIRequest("hubspot/deal", {}, { method: "POST", body: value }).then((response: HubspotDealInfo) => {
      setHubspotDealInfo(response);
      if(!orderId && !accountId) {
        orderProducts.account_info = response.company;
        orderProducts.hubspot_deal_id = response.deal.hubspot_id;
        setOrderProducts({ ...orderProducts });
      }
    }).catch((reason: APIError) => {
      console.error("hubspot deal API error", reason);
      setHubspotDealInfo(undefined);
      setHubspotInfoError(reason.server_message || "Lookup error");
    }).finally(() => {
      setHubspotInfoLoading(false);
      setIsInitializing(false);
    });
  };
  
  const handleSigning = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSigningInProgress(true);
    setSigningError(undefined);
    
    const formData = new FormData(event.currentTarget);
    const formJson = JSON.stringify(Object.fromEntries(formData.entries()), (_, value) => {
      if (value === "") return undefined;
      return value;
    });
    
    APIRequest(`billing/order/sign/${orderId}`, {}, { method: "POST", body: formJson }).then(() => {
      loadOrder(orderId);
    }).catch((reason: APIError) => {
      console.error("Signing error", reason);
      setSigningError(reason.message);
    }).finally(() => {
      setSigningInProgress(false);
    });
  };
  
  // Compute totals for the order, for display and confirmation
  const computeOrderTotals = (order: OrderProducts) => {
    interface PricingTotals { subtotal: number; discount: number; total: number; }
    const totals = {
      products: {subtotal: 0, discount: 0, total: 0},
      subscriptions: {subtotal: 0, discount: 0, total: 0},
      api: {subtotal: 0, discount: 0, total: 0}
    }
    
    // These multiply terms for pricing display
    // For example, a two-year term gets us twice the annual value, so we multiply by 2.0
    //   whereas a 6 month term is just half the annual value, so we multiply by 0.5
    // This should only be applied to subscriptions, not products (as products are one-time charged, not recurring, and have no term)
    const termScalers = {
      "P1M":  0.08333333333333333,
      "P3M":  0.25,
      "P6M":  0.5,
      "P9M":  0.75,
      "P1Y":  1.0,
      "P18M": 1.5,
      "P2Y":  2.0,
      "P3Y":  3.0
    };
    const termScaler = termScalers[order.subscription_term as keyof typeof termScalers] ?? termScalers.P1Y;
    
    function addPrice(input: OrderProduct | OrderPlan, output: PricingTotals, isSub: boolean) {
      const basePrice = input.base_price * (isSub ? termScaler : 1.0);
      const quantity = 'quantity' in input ? input.quantity || 1 : 1;
      output.subtotal += basePrice * quantity;
      
      const discount = (input.discount_type === 'currency' ? input.discount_amount : (input.discount_amount / 100 * input.base_price)) * (isSub ? termScaler : 1.0);
      output.discount += discount * quantity;
      output.total += basePrice - discount;
    }
    
    if(order.products && order.products.length > 0) {
      order.products.map((prod) => {
        addPrice(prod, totals.products, false);
      });
    }
    
    if(order.account_plan) {
      addPrice(order.account_plan, totals.subscriptions, true);
    }
    if(order.social_plan) {
      addPrice(order.social_plan, totals.subscriptions, true);
    }
    if(order.addon_plans && order.addon_plans.length > 0) {
      order.addon_plans.map((plan) => {
        addPrice(plan, totals.subscriptions, true);
      });
    }
    
    if(order.api_pricing) {
      const apiTermScalers = {
        "P1M":  1.0,
        "P3M":  1.0,
        "P6M":  1.0,
        "P9M":  1.0,
        "P1Y":  1.0,
        "P18M": 1.5,
        "P2Y":  2.0,
        "P3Y":  3.0
      };
      const apiTermScaler = apiTermScalers[order.subscription_term as keyof typeof apiTermScalers] ?? apiTermScalers.P1Y;
      
      Object.values(order.api_pricing).map((pricing) => {
        totals.api.subtotal += pricing.allotment.count * pricing.price * apiTermScaler;
        totals.api.discount += Math.max((pricing.allotment.count * pricing.price) - pricing.allotment.price, 0) * apiTermScaler;
        totals.api.total += pricing.allotment.price * apiTermScaler;
      });
    }
    
    return totals;
  };
  const orderTotals = computeOrderTotals(orderProducts);
  const sourceTotals = sourceOrder ? computeOrderTotals(sourceOrder) : undefined;
  
  const saveOrder = () => {
    setSaveState('saving');
    setSaveError(undefined);
    
    if(accountId) {
      // save the given account ID
      orderProducts.account_id = accountId;
    }
    
    // We want to clean up the order, removing things only used for the UI
    const serializedOrder = JSON.stringify(orderProducts, (key, value) => {
      if (key[0] === '_')
        return undefined;
      if (key === 'allotment' && !Number.isFinite(value)) {
        if (!value.count && !value.price)
          return undefined;
      }
      if (key === 'roles' && (!value || Object.keys(value).length === 0)) {
        return undefined;
      }
      return value;
    });
    
    const params: { [key: string]: string } = {};
    if(changeType) {
      params['change'] = changeType;
      params['order_id'] = (accountInfo?.billing_order_id ? accountInfo?.billing_order_id : accountInfo?.trial_order_id) || '';
    }
    
    if (!orderId) {
      APIRequest('billing/order', params, { method: 'POST', body: serializedOrder }, true)
        .then((response: { id: string }) => {
          window.open(`/admin/billing/order/${response.id}`);
        })
        .catch((error: APIError) => {
          console.error('API error saving order', error);
          setSaveState(undefined);
          setSaveError(error.server_message || error.message || error.body);
        })
    } else {
      // Existing order to update
      APIRequest(`billing/order/${orderId}`, {}, { method: 'PUT', body: serializedOrder }, true).then(() => {
        setOriginalOrder(cloneDeep(orderProducts));
      }).catch((error: APIError) => {
        console.error('API error updating order', error);
        setSaveError(error.server_message || error.message || error.body);
      }).finally(() => setSaveState(undefined));
    }
  }
  
  const approveOrder = () => {
    setSaveState('saving');
    setSaveError(undefined);
    
    APIRequest(`billing/order/approve/${orderId}`, {}, { method: 'POST' }, true).then(() => {
      const approvedAt = dayjs(new Date()).toISOString();
      setOrderProducts({ ...orderProducts, _approved_at: approvedAt });
      if(originalOrder) setOriginalOrder({ ...originalOrder, _approved_at: approvedAt });
      loadOrder(orderId);
      window.scrollTo(0, 0);
    }).catch((error: APIError) => {
      console.error('API error approving order', error);
      setSaveError(error.server_message || error.message || error.body);
    }).finally(() => setSaveState(undefined));
  }
  
  const unapproveOrder = () => {
    setSaveState('saving');
    setSaveError(undefined);
    
    APIRequest(`billing/order/unapprove/${orderId}`, {}, { method: 'POST' }, true).then(() => {
      setOrderProducts({ ...orderProducts, _approved_at: undefined });
      if(originalOrder) setOriginalOrder({ ...originalOrder, _approved_at: undefined });
    }).catch((error: APIError) => {
      console.error('API error un-approving order', error);
      setSaveError(error.server_message || error.message || error.body);
    }).finally(() => setSaveState(undefined));
  }
  
  const deleteOrder = () => {
    setSaveState('saving');
    setSaveError(undefined);
    
    APIRequest(`billing/order/${orderId}`, {}, { method: 'DELETE' }, true).then(() => {
      navigate("/admin/resources/billing-order");
    }).catch((error: APIError) => {
      console.error('API error deleting order', error);
      setSaveError(error.server_message || error.message || error.body);
      setSaveState(undefined);
    });
  }
  
  const updateInvoiceFields = () => {
    if (!editableInvoiceFields)
      return;
    
    setSaveState('saving');
    setSaveError(undefined);
    
    // Make a diff
    const existingFields = getEditableInvoiceFieldsFromOrder(orderProducts);
    const diff = JSON.stringify({
      bill_to_name: existingFields.bill_to_name === editableInvoiceFields.bill_to_name ? undefined : editableInvoiceFields.bill_to_name,
      bill_to_emails: existingFields.bill_to_emails === editableInvoiceFields.bill_to_emails ? undefined : editableInvoiceFields.bill_to_emails,
      api_notification_emails: existingFields.api_notification_emails === editableInvoiceFields.api_notification_emails ? undefined : editableInvoiceFields.api_notification_emails,
      po_number: existingFields.po_number === editableInvoiceFields.po_number ? undefined : editableInvoiceFields.po_number,
      billing_address: existingFields.billing_address === editableInvoiceFields.billing_address ? undefined : editableInvoiceFields.billing_address,
      memo: existingFields.memo === editableInvoiceFields.memo ? undefined : editableInvoiceFields.memo,
    });
    
    APIRequest(`billing/order/invoice-fields/${orderId}`, {}, { method: 'PUT', body: diff }, true).then(() => {
      loadOrder(orderId);
      window.scrollTo(0, 0);
    }).catch((error: APIError) => {
      console.error('API error updating invoice fields', error);
      setSaveError(error.server_message || error.message || error.body);
    }).finally(() => setSaveState(undefined));
  }
  
  // Determine the page title
  let pageTitle = "New Account Order";
  if(orderProducts.account_id) {
    if(changeType === 'trial') {
      pageTitle = `Trial Order for ${accountInfo?.name || orderProducts._bill_to_name}`;
    } else if(changeType === 'change') {
      pageTitle = `Change Order for ${accountInfo?.name || orderProducts._bill_to_name}`;
    } else if(changeType === 'renewal') {
      pageTitle = `Renewal Order for ${accountInfo?.name || orderProducts._bill_to_name}`;
    } else {
      pageTitle = `Account Re-Activation Order for ${accountInfo?.name || orderProducts._bill_to_name}`;
    }
  } else if(accountId) {
    pageTitle = `Account Re-Activation Order for ${accountInfo?.name || orderProducts._bill_to_name}`;
  }

  // Initialization failed
  if (initError) {
    return (
      <>
        <TopBar title={pageTitle} showLogo={true}></TopBar>
        <Alert style={{ margin: "1rem" }} severity="error">
          {"Initialization error: " + initError}
        </Alert>
      </>
    );
  }

  // Initialization isn't done yet, don't try to render yet
  if (!infegyUsers || !billingProducts || (accountId && !accountInfo) || isInitializing) {
    return (
      <>
        <TopBar title={pageTitle} showLogo={true}></TopBar>
        <Alert style={{ margin: "1rem" }} severity="info">
          <CircularProgress size="1rem" /> {"Initializing..."}
        </Alert>
      </>
    );
  }

  const availableAddonPlans = (
    !orderProducts.addon_plans || orderProducts.addon_plans.length === 0
      ? billingProducts.addon_plans
      : // Filter out existing selections, as each can only be added once
        billingProducts.addon_plans.filter(
          (bp) => !orderProducts.addon_plans?.find((op) => bp.id === op.id),
        )
  // filter out plans that require social, if we don't have a social plan
  ).filter((p) => orderProducts.social_plan || !p.requires_social_plan);
  
  // Utility to update reps
  function updateReps(key: 'lead_source'|'lead_chase'|'account_exec'|'solutions_consultant'|'client_success', users: Array<SimpleUser> | undefined) {
    if(!orderProducts.reps) {
      orderProducts.reps = {}
    }
    if(!users || users?.length === 0) {
      delete orderProducts.reps[key];
      if(Object.keys(orderProducts.reps).length === 0) {
        delete orderProducts.reps;
      }
    } else {
      orderProducts.reps[key] = users;
    }
    setOrderProducts({ ...orderProducts });
  }
 
  // Utility that retuns true if the save button should be enabled
  function isSaveEnabled(): boolean {
    if(saveState !== undefined) {
      return false;
    }
    if (orderProducts._send_order_by_email && (!orderProducts.order_email || orderProducts.order_email.length < 5)) {
      return false;
    }
    if(originalOrder && isEqual(originalOrder, orderProducts)) {
      return false;
    }
    if(orderProducts.account_plan) {
      if(!accountId && !orderProducts.account_id) {
        if(!orderProducts.account_info || !orderProducts.account_info?.name || !orderProducts.account_info?.domain) {
          return false;
        }
        if(!orderProducts.admin_email || !orderProducts.admin_role) {
          return false;
        }
      }
      return true;
    } else if(orderProducts.products && orderProducts.products?.length > 0) {
      return true;
    }
    return false;
  }
  
  const invoiceFieldsHaveChanges = !isEqual(getEditableInvoiceFieldsFromOrder(orderProducts), editableInvoiceFields);
  
  // Determine which message we should show at the top
  let alertMessage: string | null = null;
  let alertSeverity: 'success'|'warning'|'error' = "success";
  if(isReadOnly) {
    if (orderProducts._signed_at) {
      alertMessage = `This order was signed ${dayjs(orderProducts._signed_at).fromNow()}`;
      alertSeverity = "success";
    } else if(orderProducts._approved_at) {
      if(orderProducts._send_order_by_email && !orderProducts._sent_at) {
        alertMessage = `Order is approved, and is now read-only. It will be sent by email to ${orderProducts.order_email}`;
      } else if(orderProducts._sent_at) {
        alertMessage = `Order is approved, and is now read-only. It has been sent by email ${dayjs(orderProducts._sent_at).fromNow()} to ${orderProducts.order_email}`;
      } else {
        alertMessage = "Order is approved, and is now read-only. It will NOT be sent by email.";
      }
      alertSeverity = "warning";
    } else if(!isSuperAdmin) {
      alertMessage = "Order is read-only, awaiting approval.";
      alertSeverity = "warning";
    } else {
      alertMessage = "Order is read-only.";
      alertSeverity = "warning";
    }
  } else {
    if(!orderId) {
      if(accountId) {
        if(changeType === 'trial') {
          alertMessage = `Temporarily change service for ${accountInfo?.name || orderProducts._bill_to_name} free of charge.`;
        } else if(changeType === 'change') {
          alertMessage = `Change service for ${accountInfo?.name || orderProducts._bill_to_name}, potentially generating a pro-rated invoice.`;
        } else if(changeType === 'renewal') {
          alertMessage = `Change services at the end of the current term, on renewal, for ${accountInfo?.name || orderProducts._bill_to_name}.`;
        } else {
          // No change / re-activating service
          alertMessage = `This will re-activate service on the existing account for ${accountInfo?.name || orderProducts._bill_to_name} with this Order.`;
        }
      } else {
        alertMessage = "This will create a new Account! New customers only.";
      }
    } else {
      alertMessage = "Editing existing order. Make any changes, then Approve or Delete.";
      alertSeverity = "warning";
    }
  }
  
  // Determine min/max values for billing and service start dates
  let minSubscriptionStart: dayjs.Dayjs | undefined = undefined, maxSubscriptionStart: dayjs.Dayjs | undefined = undefined;
  let minBillingStart: dayjs.Dayjs | undefined = undefined;
  if(accountId) {
    if(changeType === 'trial') {
      // billing selectors are hidden
      // service can start now with no max
      minSubscriptionStart = orderProducts.subscription_start_date ? dayjs.unix(Math.min(dayjs(orderProducts.subscription_start_date).unix(), dayjs(new Date()).unix())) : dayjs(new Date());
    } else if(changeType === 'change') {
      // min start date must be now, max is end of term
      // billing selections are all to be hidden
      minSubscriptionStart = dayjs(new Date());
      maxSubscriptionStart = dayjs(orderProducts._end_of_current_term);
    } else if(changeType === 'renewal') {
      // Billing selector will be hidden, service start is fixed to end of term
      minSubscriptionStart = dayjs(orderProducts.subscription_start_date);
      maxSubscriptionStart = minSubscriptionStart;
    }
  } else {
    minSubscriptionStart = orderProducts.subscription_start_date ? dayjs.unix(Math.min(dayjs(orderProducts.subscription_start_date).unix(), dayjs(new Date()).unix())) : dayjs(new Date());
    minBillingStart = (orderProducts.invoice_start_date || orderProducts.subscription_start_date) ? dayjs.unix(Math.min(dayjs(orderProducts.invoice_start_date || orderProducts.subscription_start_date).unix(), dayjs(new Date()).unix())) : dayjs(new Date());
    // max remains undefined for new orders
  }
  
  return (
    <Box sx={{ maxWidth: "1300px", margin: "0 auto", position: "relative", pl: 2, pr: 2 }}>
      <TopBar title={pageTitle} showLogo={true} />

      <Stack
        sx={{
          alignItems: "stretch",
          gap: 1
        }}>
        {alertMessage &&
          <Alert style={{ alignSelf: "center" }} severity={alertSeverity}>
            {alertMessage}
          </Alert>
        }
        
        {(changeType === 'change' || (!changeType && orderProducts.change?.type === 'change')) &&
          <Alert severity="warning">
            This is a change order. This will replace the active order on the account, generating a pro-rated invoice for any cost increases. This order should reflect the COMPLETE state of services on the Account, including full annual costs! We will compute cost differences automatically. On the Service Start Date, this order will replace the existing order, and a pro-rated invoice will be generated for any increase in costs. Please note: We do not issue refunds for price decreases.
          </Alert>
        }
        
        {(changeType === 'renewal' || orderProducts.change?.type === 'renewal') &&
          <Alert severity="warning">
            This is a renewal order. This will replace the active order on the account at the end of the current term. This order should reflect the COMPLETE state of services on the Account at the time of renewal. Please note: A renewal order is only required if you wish to change services or pricing at renewal.
          </Alert>
        }
        
        {(changeType === 'trial' || orderProducts.change?.type === 'trial') &&
          <Alert severity="warning">
            This is a trial order. This will replace the active order on the account temporarily. This order should reflect the COMPLETE state of services on the Account, as it will take over as the complete order of services while activated! On the Service Start Date, this order will replace the existing order. When the trial ends, the previous, paid order will be re-applied, restoring services to their prior state.
          </Alert>
        }
        
        {orderId &&
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "end"
            }}>
            
            <Button
              disabled={isSaveEnabled()}
              startIcon={<Preview />}
              href={makeLink(`billing/order/view/${orderId}`)}
              target='_blank'
            >
              View Order Page
            </Button>
            
            <ButtonMenu
              disabled={isSaveEnabled()}
              buttonLabel="Invoice Preview"
              startIcon={<RequestPage />}
              menuItems={[
                {name: 'Webpage', id: 'html'},
                {name: 'PDF', id: 'pdf'}
              ]}
              onSelect={(item: { id: string; name: string; }) => {
                type ParamsMap = { [key: string]: string; };
                const params: ParamsMap = {
                  'html': '',
                  'pdf': '?pdf=1'
                };
                window.open(makeLink(`billing/order/invoice-preview/${orderId}${params[item.id]}`), '_blank');
              }}
            />
            
            <ButtonMenu
              disabled={isSaveEnabled()}
              buttonLabel="Order PDF"
              startIcon={<Description />}
              menuItems={[
                {name: 'Standard PDF', id: 'st'},
                {name: 'Standard PDF (no terms)', id: 'st-nt'},
                {name: 'Signable PDF', id: 'sign'},
                {name: 'Signable PDF (no terms)', id: 'sign-nt'}
              ]}
              onSelect={(item: { id: string; name: string; }) => {
                type ParamsMap = { [key: string]: string; };
                const params: ParamsMap = {
                  'st': 'with_terms=1&with_signing_area=0',
                  'st-nt': 'with_terms=0&with_signing_area=0',
                  'sign': 'with_terms=1&with_signing_area=1',
                  'sign-nt': 'with_terms=0&with_signing_area=1'
                };
                window.open(makeLink(`billing/order/pdf/${orderId}?${params[item.id]}`), '_blank');
              }}
            />
          </Stack>
        }

        {/* First, we need a link, ID, etc. to load information on the source Deal from Hubspot 
            This also gives us information on the Hubspot Company, which we can use to default info on the Account */}
        {(!orderId || hubspotDealInfo) &&
          <Card>
            <CardContent>
              <Typography variant="h4">Hubspot Deal</Typography>
              {!orderId &&
                <TextField required variant="standard" fullWidth={true} autoFocus
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">🔗</InputAdornment>
                      ),
                    },
                  }}
                  disabled={hubspotInfoLoading || hubspotDealInfo !== undefined}
                  helperText="A link to, or the ID of, a Deal on Hubspot"
                  error={!!hubspotInfoError}
                  label={hubspotInfoError}
                  placeholder="https://app.hubspot.com/..."
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleHubspotDealChange(e.target.value)
                  }
                />
              }
  
              {/* If we are actively loading Hubspot data, say so */}
              {hubspotInfoLoading && (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    mt: 1
                  }}>
                  <CircularProgress size="1rem" />
                  <Typography sx={{
                    ml: 1
                  }}>Talking to Hubspot...</Typography>
                </Stack>
              )}
  
              {/* Display info we get from Hubspot for user confirmation */}
              {hubspotDealInfo !== undefined && (
                <HubspotDealInfoCard hubspotDealInfo={hubspotDealInfo} />
              )}
            </CardContent>
          </Card>
        }
        
        {orderProducts._signed_at && !orderProducts._trial &&
          <Card>
            <CardContent>
              <Stack sx={{
                gap: 2
              }}>
                <Typography variant="h4">Invoice Fields</Typography>
                <Typography variant="subtitle2" sx={{
                  mb: 1
                }}>
                  These are the editable fields on invoices. Update them here to persist when this Order generates invoices.<br />
                  Use the Invoice Preview function at the top of the page to preview the invoice.<br />
                  This information originated (primarily) from the client when completing the Order form.<br />
                </Typography>
                <TextField disabled={!isSuperAdmin} sx={{ maxWidth: "30em" }} size="small"
                  label="Bill To Name"
                  value={editableInvoiceFields?.bill_to_name || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    if (editableInvoiceFields) {
                      setEditableInvoiceFields({ ...editableInvoiceFields, bill_to_name: e.target.value });
                    }
                  }}
                />
                <TextField disabled={!isSuperAdmin} sx={{maxWidth: "30em"}} size="small"
                  label="Bill to Email(s) (comma-separated)"
                  value={editableInvoiceFields?.bill_to_emails || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    if (editableInvoiceFields) {
                      setEditableInvoiceFields({ ...editableInvoiceFields, bill_to_emails: e.target.value });
                    }
                  }}
                />
                <TextField disabled={!isSuperAdmin} sx={{maxWidth: "30em"}} size="small"
                  label="Addt'l API Notification Email(s) (comma-separated)"
                  value={editableInvoiceFields?.api_notification_emails || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    if (editableInvoiceFields) {
                      setEditableInvoiceFields({ ...editableInvoiceFields, api_notification_emails: e.target.value });
                    }
                  }}
                />
                <TextField disabled={!isSuperAdmin} sx={{maxWidth: "30em"}} size="small"
                  label="PO Number"
                  value={editableInvoiceFields?.po_number || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    if (editableInvoiceFields) {
                      setEditableInvoiceFields({ ...editableInvoiceFields, po_number: e.target.value });
                    }
                  }}
                />
                <TextField multiline disabled={!isSuperAdmin} sx={{maxWidth: "30em"}} size="small"
                  label="Billing Address"
                  value={editableInvoiceFields?.billing_address || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    if (editableInvoiceFields) {
                      setEditableInvoiceFields({ ...editableInvoiceFields, billing_address: e.target.value });
                    }
                  }}
                />
                <TextField multiline fullWidth disabled={!isSuperAdmin} sx={{maxWidth: "45em"}} size="small"
                  label="Memo"
                  value={editableInvoiceFields?.memo || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    if (editableInvoiceFields) {
                      setEditableInvoiceFields({ ...editableInvoiceFields, memo: e.target.value });
                    }
                  }}
                />
                {orderProducts?._signing_data?.details &&
                  <TextField multiline fullWidth disabled={true} sx={{maxWidth: "45em"}} size="small"
                    label="Signing Details"
                    value={orderProducts?._signing_data?.details}
                  />
                }
              {isSuperAdmin && <>
                {saveError &&
                  <Typography color="error" variant="h5" sx={{
                    mb: 1
                  }}>{saveError}</Typography>
                }
                <Button variant="outlined" color="success" loading={saveState === 'saving'}
                  disabled={!invoiceFieldsHaveChanges}
                  onClick={() => updateInvoiceFields()}
                >
                  Save Changes
                </Button>
              </>}
              </Stack>
            </CardContent>
          </Card>
        }

        {/* Once we have loaded Hubspot Deal info, or we're editing an existing order, we can proceed with the rest of the form */}
        {(orderId || hubspotDealInfo !== undefined || accountId) && (
          <>
            {/* This card holds the line items for invoices - subscriptions, products, pay-as-you-go... */}
            <Card>
              <CardContent>
                <TextField select label="Currency" size="small" disabled={isReadOnly}
                  value={orderProducts.currency}
                  onChange={(e) => setOrderProducts({ ...orderProducts, currency: e.target.value as 'usd'|'eur' })}
                >
                  <MenuItem value="usd">US Dollars ($)</MenuItem>
                  <MenuItem value="eur">Euro (€)</MenuItem>
                </TextField>
                <Typography
                  variant="h4"
                  sx={{
                    mt: 2,
                    mb: 2
                  }}>
                  Subscriptions
                </Typography>
                {!orderProducts.account_plan && !isReadOnly && (
                  <ButtonMenu
                    buttonLabel="Add Subscription"
                    startIcon={<EventRepeat />}
                    menuItems={billingProducts.account_plans}
                    onSelect={(plan: BillingPlan) => {
                      setOrderProducts({ ...orderProducts,
                        account_plan: {
                          id: plan.id,
                          name: plan.name,
                          description: plan.description,
                          base_price: plan.order_defaults?.price || 0,
                          _always_editable: plan._always_editable,
                          discount_type: "percent",
                          discount_amount: 0,
                          roles: Object.fromEntries(Object.entries(plan.roles).map(([key, role]) => [key, { order: role.order_defaults }]))
                        },
                        subscription_start_date: orderProducts.subscription_start_date ? orderProducts.subscription_start_date : dayjs(new Date()).toISOString(),
                        subscription_term: "P1Y",
                        invoice_frequency: "P1Y",
                        invoice_net: 30
                      });
                    }}
                  />
                )}

                {/* Render chosen plan and pricing options */}
                {orderProducts.account_plan && (
                  <Box>
                    {/* Subscription start date and term options */}
                    <Box sx={{
                      mb: 2
                    }}>
                      <Stack direction="row" sx={{
                        gap: 2
                      }}>
                        
                        <DateTimePicker label="Service Start Date" disabled={isReadOnly || changeType === 'renewal'}
                          value={orderProducts.subscription_start_date ? dayjs(orderProducts.subscription_start_date) : null}
                          minDate={minSubscriptionStart}
                          maxDate={maxSubscriptionStart}
                          onChange={(val: dayjs.Dayjs | null) => {
                            if (val && !val.toString().startsWith("Invalid")) {
                              setOrderProducts({ ...orderProducts, subscription_start_date: val.toISOString() });
                            }
                          }}
                        />

                        {!orderProducts._trial && (
                          <>
                            <TextField select label="Term" disabled={termsReadOnly}
                              value={orderProducts.subscription_term}
                              onChange={(e) => setOrderProducts({ ...orderProducts, subscription_term: e.target.value })}
                            >
                              <MenuItem value="P1M">1 month</MenuItem>
                              <MenuItem value="P3M">3 months</MenuItem>
                              <MenuItem value="P6M">6 months</MenuItem>
                              <MenuItem value="P1Y">1 year</MenuItem>
                              <MenuItem value="P18M">18 months</MenuItem>
                              <MenuItem value="P2Y">2 years</MenuItem>
                              <MenuItem value="P3Y">3 years</MenuItem>
                            </TextField>

                            <TextField select label="Invoicing" disabled={termsReadOnly}
                              value={orderProducts.invoice_frequency}
                              onChange={(e) => setOrderProducts({ ...orderProducts, invoice_frequency: e.target.value })}
                            >
                              <MenuItem value="P1M">Monthly</MenuItem>
                              <MenuItem value="P3M">Quarterly</MenuItem>
                              <MenuItem value="P6M">Bi-Annual</MenuItem>
                              <MenuItem value="P1Y">Annual</MenuItem>
                            </TextField>
                            
                            <TextField label="Net" disabled={termsReadOnly}
                              style={{ maxWidth: "5em" }}
                              value={orderProducts.invoice_net?.toString()}
                              type="number"
                              onChange={(e) => setOrderProducts({ ...orderProducts, invoice_net: Math.min(Math.max(parseInt(e.target.value) || 1, 1), 365) })}
                            />
                            
                            <DateTimePicker label="Billing Start Date" disabled={termsReadOnly || changeType === 'renewal'}
                              value={orderProducts.invoice_start_date ? 
                                dayjs(orderProducts.invoice_start_date) : (orderProducts.subscription_start_date ? dayjs(orderProducts.subscription_start_date) : null)}
                              minDate={minBillingStart}
                              onChange={(val: dayjs.Dayjs | null) => {
                                if (val && !val.toString().startsWith("Invalid")) {
                                  setOrderProducts({ ...orderProducts, invoice_start_date: val.toISOString() });
                                }
                              }}
                            />
                          
                            <FormControlLabel label={orderProducts.auto_renew === false ? "NO auto-renew" : "Auto-Renew"}
                              control={
                                <Switch disabled={termsReadOnly}
                                  defaultChecked={orderProducts.auto_renew === false ? false : true}
                                  value={orderProducts.auto_renew === false ? false : true}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => setOrderProducts({ ...orderProducts, auto_renew: checked })}
                                />
                              }
                            />
                          
                          </>
                        )}
                      </Stack>

                      {changeType !== 'renewal' &&
                        <Typography variant="subtitle2" sx={{
                          mt: 1
                        }}>
                          If this date passes, the {orderProducts._trial ? "trial" : "order"} will activate on approval.
                        </Typography>
                      }
                      
                      {orderProducts.invoice_start_date && orderProducts.subscription_start_date && dayjs(orderProducts.invoice_start_date) > dayjs(orderProducts.subscription_start_date) &&
                        <Typography color="warning" sx={{
                          maxWidth: "50em"
                        }}>
                          Billing starts <em>after</em> service! This effectively gives the client {dayjs(orderProducts.invoice_start_date).diff(dayjs(orderProducts.subscription_start_date), 'day')} days of free service!<br/>The Term duration will begin at the Billing start date, when the first Invoice is generated.
                        </Typography>
                      }
                    </Box>

                    {/* Trial toggle */}
                    {(changeType === 'trial' || !changeType) &&
                      <Box sx={{
                        mb: 2
                      }}>
                        <FormGroup>
                          <Stack direction="row">
                            <FormControlLabel label={`Trial ${orderProducts._trial ? "enabled" : "disabled"}`}
                              control={
                                <Switch disabled={isReadOnly || changeType === 'trial'}
                                  value={!!orderProducts._trial} defaultChecked={orderProducts._trial ? true : undefined}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                    checked: boolean,
                                  ) => {
                                    if (!checked) {
                                      setOrderProducts({ ...orderProducts, _trial: undefined, trial_duration: undefined });
                                    } else {
                                      setOrderProducts({ ...orderProducts, _trial: true, trial_duration: 5 });
                                    }
                                  }}
                                />
                              }
                            />

                            {orderProducts._trial && (
                              <TextField disabled={isReadOnly}
                                label="Duration (days)"
                                type="number"
                                size="small"
                                value={orderProducts.trial_duration || 1}
                                onChange={(e) => {
                                  const duration = Math.max(1, Math.min(parseInt(e.target.value) || 1, 14));
                                  setOrderProducts({ ...orderProducts, trial_duration: duration });
                                }}
                                sx={{ width: 100 }}
                              />
                            )}
                          </Stack>
                          {orderProducts._trial && (
                            <Box>
                              <Typography variant="subtitle2">
                                Invoicing is disabled, effectively ignoring pricing for this order.
                                Additionally, this order will not require customer approval. It is still recommended to
                                complete the Order as if it was the final order, as it can be copied to create a paid order.
                              </Typography>
                            </Box>
                          )}
                        </FormGroup>
                      </Box>
                    }

                    <Typography variant="h5" sx={{
                      mb: 1
                    }}>Account Plan</Typography>
                    <PlanOrderEditor currency={orderProducts.currency} disabled={isReadOnly}
                      plan={orderProducts.account_plan}
                      billingPlan={billingProducts._plansById[orderProducts.account_plan.id]}
                      onDelete={() => setOrderProducts({ ...orderProducts, account_plan: undefined })}
                      onChange={(plan: OrderPlan | OrderProduct) => setOrderProducts({ ...orderProducts, account_plan: plan as OrderPlan })}
                      allowEditing={!orderProducts._approved_at}
                    />
                    
                    {/* Seat pricing and allocation */}
                    <Typography variant="h5" sx={{
                      mt: 2
                    }}>Seat Allocations and Pricing</Typography>
                    <Typography variant="subtitle2" sx={{
                      mb: 1
                    }}>Seat pricing is for additional seats purchased <em>after</em> the order. Please update the Account Plan price for cost increases due to added seat allotments!</Typography>
                    <Stack
                      direction="row"
                      sx={{
                        gap: 3,
                        marginBottom: 2
                      }}>
                      {Object.entries(orderProducts.account_plan.roles)
                        .filter(([key, role]) => (role.order?.allotment || 0) > 0 || !key.startsWith('ai-') || orderProducts.addon_plans?.some(plan => plan.id === 'ai'))
                        .map(([key, role]) => (
                          <Stack
                            key={`role-${key}`}
                            sx={{
                              gap: 1.5,
                              mb: 1
                            }}>
                            <Typography>{billingProducts.roles[key].name}</Typography>
                            <TextField disabled={isReadOnly}
                              label="Annual Price Ea."
                              type="number"
                              size="small"
                              value={role.order?.price || 0}
                              onChange={(e) => {
                                if (!orderProducts.account_plan?.roles[key].order) return;
                                orderProducts.account_plan.roles[key].order.price = parseFloat(e.target.value) || 0;
                                setOrderProducts({ ...orderProducts });
                              }}
                              slotProps={{
                                input: {
                                  startAdornment: (
                                    <InputAdornment position="start">{currencySymbol}</InputAdornment>
                                  ),
                                },
                              }}
                              sx={{ width: 120 }}
                            />
                            <TextField disabled={isReadOnly}
                              label="Allotment"
                              type="number"
                              size="small"
                              value={role.order?.allotment || 0}
                              onChange={(e) => {
                                if (!orderProducts.account_plan?.roles[key].order) return;
                                orderProducts.account_plan.roles[key].order.allotment = parseInt(e.target.value) || 0;
                                setOrderProducts({ ...orderProducts });
                              }}
                              sx={{ width: 120 }}
                            />
                          </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* We need an account plan before we can choose a Social plan */}
                {orderProducts.account_plan && !orderProducts.social_plan && !isReadOnly && (
                  <Box sx={{
                    mb: 1
                  }}>
                    <ButtonMenu
                      buttonLabel="Add Social Plan"
                      startIcon={<ConnectWithoutContact />}
                      menuItems={billingProducts.social_plans}
                      onSelect={(plan: BillingPlan) => {
                        orderProducts.social_plan = {
                          id: plan.id,
                          name: plan.name,
                          description: plan.description,
                          _always_editable: plan._always_editable,
                          base_price: plan.order_defaults?.price || 0,
                          discount_type: "percent",
                          discount_amount: 0,
                          roles: {}, // We dont need to store roles for anything but the primary account plan
                        };
                        
                        // Adjust role pricing, if this plan has any such adjustments
                        Object.entries(plan.roles).map(([key, role]) => {
                          const roleOrder = orderProducts.account_plan?.roles[key].order;
                          if(role.order_defaults?.price && roleOrder?.price)
                            roleOrder.price += role.order_defaults.price;
                        });
                        
                        setOrderProducts({ ...orderProducts });
                      }}
                    />
                  </Box>
                )}
                {orderProducts.account_plan && orderProducts.social_plan && (
                  <>
                    <Typography variant="h5" sx={{
                      mb: 1
                    }}>
                      Social Plan
                    </Typography>
                    <PlanOrderEditor currency={orderProducts.currency} disabled={isReadOnly}
                      plan={orderProducts.social_plan}
                      billingPlan={billingProducts._plansById[orderProducts.social_plan.id]}
                      onDelete={() => setOrderProducts({ ...orderProducts, social_plan: undefined })}
                      onChange={(plan: OrderPlan | OrderProduct) => setOrderProducts({ ...orderProducts,social_plan: plan as OrderPlan })}
                      allowEditing={!orderProducts._approved_at}
                    />
                  </>
                )}

                {orderProducts.account_plan && orderProducts.addon_plans && orderProducts.addon_plans.length > 0 && (
                  <Stack sx={{
                    gap: 2
                  }}>
                    <Typography variant="h5" sx={{
                      mb: 1
                    }}>
                      Add-on Plans
                    </Typography>
                    {orderProducts.addon_plans.map((plan, idx) => (
                      <PlanOrderEditor currency={orderProducts.currency} disabled={isReadOnly}
                        key={`plan-addon-${idx}-${plan.id}`}
                        plan={plan}
                        billingPlan={billingProducts._plansById[plan.id]}
                        allowEditing={!orderProducts._approved_at}
                        onDelete={() => {
                          if (!orderProducts.addon_plans || orderProducts.addon_plans.length === 0 )
                            return;
                          if (orderProducts.addon_plans.length === 1) {
                            orderProducts.addon_plans = undefined;
                          } else {
                            orderProducts.addon_plans.splice(idx, 1);
                          }
                          setOrderProducts({ ...orderProducts });
                        }}
                        onChange={(plan: OrderPlan | OrderProduct) => {
                          if (!orderProducts.addon_plans) return;
                          orderProducts.addon_plans[idx] = plan as OrderPlan;
                          setOrderProducts({ ...orderProducts });
                        }}
                      />
                    ))}
                  </Stack>
                )}

                {orderProducts.account_plan && availableAddonPlans.length > 0 && !isReadOnly && (
                  <ButtonMenu
                    buttonLabel="Add Add-on Plan"
                    startIcon={<AddLink />}
                    menuItems={availableAddonPlans}
                    onSelect={(plan: BillingPlan) => {
                      if (!orderProducts.addon_plans)
                        orderProducts.addon_plans = [];

                      orderProducts.addon_plans?.push({
                        id: plan.id,
                        name: plan.name,
                        description: plan.description,
                        _always_editable: plan._always_editable,
                        base_price: plan.order_defaults?.price || 0,
                        discount_type: "percent",
                        discount_amount: 0,
                        roles: {} // We dont need to store roles for anything but the primary account plan
                      });
                      
                      if(plan.roles) {
                        // Adjust role pricing, if this plan has any such adjustments
                        Object.entries(plan.roles).map(([key, role]) => {
                          const roleOrder = orderProducts.account_plan?.roles[key].order;
                          if(role.order_defaults?.price && roleOrder?.price)
                            roleOrder.price += role.order_defaults.price;
                        });
                      }
                      
                      setOrderProducts({ ...orderProducts });
                    }}
                  />
                )}
                
                {orderProducts.account_plan && <Box>
                  {/* API pricing and allocation */}
                  <Typography variant="h5" sx={{
                    mt: 2
                  }}>API Allocations and Pricing</Typography>
                  {(changeType === 'change' || orderProducts.change?.type === 'change') &&
                    <Typography
                      variant="subtitle2"
                      color="warning"
                      sx={{
                        mt: 0.5,
                        mb: 0.5
                      }}>CAREFUL! This is a change order. API allocationd are pre-paid for the term length. DO NOT DECREASE pre-purchased volume! Nor prices - we do not issue refunds. You can increase allotments and pre-purchase prices, or add categories, and the difference will be billed on the first invoice for this order. PaYG prices can be changed and will affect the next PaYG invoice.</Typography>
                  }
                  <Typography variant="subtitle2" sx={{
                    mb: 1
                  }}>API Price/per is used for Pay-as-You-Go (PaYG) pricing calculations. Pre-Purchase Volume and Pre-Purchase Price are for pre-purchased allotments of API access on this order. Any such non-zero allotments will be listed on the order form, and on the first invoice.<br />These pre-purchased allocations are for the term length, or 1 year, whichever is less. Meaning if this is a multi-year term, the pre-purchase values below are per year!</Typography>
                  <Stack direction="row" sx={{
                    gap: 2
                  }}>
                    {Object.entries(orderProducts.api_pricing)
                      .filter(([key, api_el]) => (api_el.allotment.count || 0) > 0 || (api_el.allotment.price || 0) > 0 || !key.startsWith('ai_') || orderProducts.addon_plans?.some(plan => plan.id === 'ai'))
                      .map(([key, api_el]) => (
                      <Stack
                        key={`role-${key}`}
                        sx={{
                          gap: 1.5,
                          mb: 1
                        }}>
                        <Typography>{billingProducts.api_pricing[key].name}</Typography>
                        <TextField disabled={isReadOnly}
                          label={`PaYG Price/${billingProducts.api_pricing[key].per}`}
                          type="number"
                          size="small"
                          value={api_el.price}
                          onChange={(e) => {
                            orderProducts.api_pricing[key].price = parseFloat(e.target.value) || 0;
                            setOrderProducts({ ...orderProducts });
                          }}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">{currencySymbol}</InputAdornment>
                              ),
                            },
                          }}
                        />
                        <TextField disabled={isReadOnly}
                          label="Pre-purchase volume"
                          //type="number"
                          size="small"
                          value={new Intl.NumberFormat('en-US').format(api_el.allotment.count || 0)}
                          onChange={(e) => {
                            const prevExpectedPrice = orderProducts.api_pricing[key].allotment.count * orderProducts.api_pricing[key].price;
                            
                            orderProducts.api_pricing[key].allotment.count = parseInt(e.target.value.replace(/,/g, '')) || 0;
                            
                            // Does it look like price was not modified?
                            if (Math.abs(prevExpectedPrice - orderProducts.api_pricing[key].allotment.price) < 1) {
                              // Update price
                              orderProducts.api_pricing[key].allotment.price = orderProducts.api_pricing[key].allotment.count * orderProducts.api_pricing[key].price;
                            }
                            setOrderProducts({ ...orderProducts });
                          }}
                        />
                        <TextField disabled={isReadOnly}
                          label="Pre-Purchase Price"
                          type="text"
                          size="small"
                          value={new Intl.NumberFormat('en-US').format(api_el.allotment?.price || 0)}
                          onChange={(e) => {
                            orderProducts.api_pricing[key].allotment.price = parseFloat(e.target.value.replace(/,/g, '')) || 0;
                            setOrderProducts({ ...orderProducts });
                          }}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">{currencySymbol}</InputAdornment>
                              ),
                            },
                          }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Box>}

                {!orderProducts._trial && <>
                  {/* Products (trials cannot have products) */}
                  {orderProducts.products && orderProducts.products.length > 0 &&
                    <Typography
                      variant="h4"
                      sx={{
                        mt: 3,
                        mb: 2
                      }}>
                      Products (one-time charges)
                    </Typography>
                  }
                  {!(orderProducts.products && orderProducts.products.length > 0) && /* spacing if no Products label */
                    <Box sx={{
                      margin: 2
                    }}></Box>
                  }
                  {orderProducts.products && orderProducts.products.length > 0 && (
                    <Stack sx={{
                      gap: 1
                    }}>
                      {orderProducts.products.map((product, idx) => (
                        <PlanOrderEditor currency={orderProducts.currency} disabled={isReadOnly}
                          isProduct={true}
                          key={`product-${idx}`}
                          plan={product}
                          allowEditing={!orderProducts._approved_at}
                          onDelete={() => {
                            if (!orderProducts.products || orderProducts.products.length === 0)
                              return;
                            if (orderProducts.products.length === 1) {
                              orderProducts.products = undefined;
                            } else {
                              orderProducts.products.splice(idx, 1);
                            }
                            setOrderProducts({ ...orderProducts });
                          }}
                          onChange={(product: OrderPlan | OrderProduct) => {
                            if (!orderProducts.products) return;
                            orderProducts.products[idx] = product as OrderProduct;
                            setOrderProducts({ ...orderProducts });
                          }}
                        />
                      ))}
                    </Stack>
                  )}

                  {!isReadOnly &&
                    <ButtonMenu
                      buttonLabel="Add Product"
                      startIcon={<AddShoppingCart />}
                      menuItems={billingProducts.products}
                      onSelect={(product: BillingProduct) => {
                        if (!orderProducts.products)
                          orderProducts.products = [];

                        orderProducts.products.push({
                          name: product.name,
                          description: product.description,
                          _always_editable: product._always_editable,
                          quantity: 1,
                          base_price: product.order_defaults?.price || 1,
                          discount_type: "percent",
                          discount_amount: 0,
                        });
                        setOrderProducts({ ...orderProducts });
                      }}
                    />
                  }
                </>}
                
                {/* Pricing totals */}
                <Stack
                  direction="row"
                  sx={{
                    gap: 5,
                    alignItems: "end",
                    justifyContent: "end"
                  }}>
                  {orderProducts.products && orderProducts.products.length > 0 && (
                    <Stack
                      sx={{
                        gap: 0.5,
                        alignItems: "end"
                      }}>
                      <Stack
                        direction="row"
                        sx={{
                          justifyContent: "space-between",
                          width: "100%",
                          gap: 1
                        }}>
                        <Typography>One-time Sub-Total:</Typography>
                        <Typography sx={{
                          fontFamily: "monospace"
                        }}>{formatCurrency(orderTotals.products.subtotal, orderProducts.currency)}</Typography>
                      </Stack>
                      
                      {orderTotals.products.discount > 0 && (
                        <Stack
                          direction="row"
                          sx={{
                            justifyContent: "space-between",
                            width: "100%",
                            gap: 1
                          }}>
                          <Typography>One-time Discount:</Typography>
                          <Typography sx={{
                            fontFamily: "monospace"
                          }}>{formatCurrency(orderTotals.products.discount, orderProducts.currency)}</Typography>
                        </Stack>
                      )}
                      
                      <Stack
                        direction="row"
                        sx={{
                          justifyContent: "space-between",
                          width: "100%",
                          gap: 1
                        }}>
                        <Typography variant="h5">One-time Total:</Typography>
                        <Typography variant="h5" sx={{
                          fontFamily: "monospace"
                        }}>{formatCurrency(orderTotals.products.total, orderProducts.currency)}</Typography>
                      </Stack>
                    </Stack>
                  )}
                  
                  {orderProducts.account_plan && (
                    <Stack
                      sx={{
                        gap: 0.5,
                        alignItems: "end"
                      }}>
                      <Stack
                        direction="row"
                        sx={{
                          justifyContent: "space-between",
                          width: "100%",
                          gap: 1
                        }}>
                        <Typography>Subscription Sub-Total:</Typography>
                        <Typography sx={{
                          fontFamily: "monospace"
                        }}>{formatCurrency(orderTotals.subscriptions.subtotal, orderProducts.currency)}</Typography>
                      </Stack>
                      
                      {orderTotals.subscriptions.discount > 0 && (
                        <Stack
                          direction="row"
                          sx={{
                            justifyContent: "space-between",
                            width: "100%",
                            gap: 1
                          }}>
                          <Typography>Subscription Discount:</Typography>
                          <Typography sx={{
                            fontFamily: "monospace"
                          }}>{formatCurrency(orderTotals.subscriptions.discount, orderProducts.currency)}</Typography>
                        </Stack>
                      )}
                      
                      {orderTotals.api.total > 0 && (
                        <Stack
                          direction="row"
                          sx={{
                            justifyContent: "space-between",
                            width: "100%",
                            gap: 1
                          }}>
                          <Typography>API Sub-Total:</Typography>
                          <Typography sx={{
                            fontFamily: "monospace"
                          }}>{formatCurrency(orderTotals.api.subtotal, orderProducts.currency)}</Typography>
                        </Stack>
                      )}
                      
                      {orderTotals.api.discount > 0 && (
                        <Stack
                          direction="row"
                          sx={{
                            justifyContent: "space-between",
                            width: "100%",
                            gap: 1
                          }}>
                          <Typography>API Discount:</Typography>
                          <Typography sx={{
                            fontFamily: "monospace"
                          }}>{formatCurrency(orderTotals.api.discount, orderProducts.currency)}</Typography>
                        </Stack>
                      )}
                      
                      <Stack
                        direction="row"
                        sx={{
                          justifyContent: "space-between",
                          width: "100%",
                          gap: 1
                        }}>
                        <Typography variant="h5">Subscription Total:</Typography>
                        <Typography variant="h5" sx={{
                          fontFamily: "monospace"
                        }}>{formatCurrency(orderTotals.subscriptions.total + orderTotals.api.total, orderProducts.currency)}</Typography>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
                
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "end",
                    width: "100%",
                    gap: 1,
                    mt: 2
                  }}>
                  <Typography variant="h3">Order Value:</Typography>
                  <Typography variant="h3" color={theme.colors.namedColors.positiveDark}>{formatCurrency(orderTotals.subscriptions.total+orderTotals.products.total+orderTotals.api.total, orderProducts.currency)}</Typography>
                </Stack>
                
                {changeType != "trial" && changeType && sourceTotals &&
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "end",
                      width: "100%",
                      gap: 1,
                      mt: 2
                    }}>
                    <Typography variant="h3">Value Change:</Typography>
                    
                    <Typography variant="h3" color={theme.colors.namedColors.positiveDark}>{formatCurrency(
                      (orderTotals.subscriptions.total+orderTotals.products.total+orderTotals.api.total) - (sourceTotals.subscriptions.total+sourceTotals.api.total)
                      , orderProducts.currency)}</Typography>
                  </Stack>
                }
              </CardContent>
            </Card>
            
            {/* If we have an account plan, we want cards to set Account info, and to invite an admin */}
            {!accountId && !orderProducts.account_id && orderProducts.account_plan && <>
              {/* A section for Account info */}
              <Card>
                <CardContent>
                  <Stack sx={{
                    gap: 2
                  }}>
                    <Typography variant="h4">Account Info</Typography>
                    <Typography variant="subtitle2">
                      We will create an Account in Starscape for this order. Provide the name and domain we should use, defaulting to information pulled from Hubspot.
                    </Typography>
                    <TextField required variant="standard" label="Name" sx={{ width: "50%", maxWidth: "20em" }}
                      disabled={isReadOnly}
                      error={!orderProducts.account_info?.name}
                      value={orderProducts.account_info?.name}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        if (!orderProducts.account_info) {
                          orderProducts.account_info = { name: e.target.value };
                        } else orderProducts.account_info.name = e.target.value;
                        setOrderProducts({ ...orderProducts });
                      }}
                    />
                    <TextField required variant="standard" label="domain" sx={{ width: "25%", maxWidth: "15em" }}
                      disabled={isReadOnly}
                      error={!orderProducts.account_info?.domain}
                      value={orderProducts.account_info?.domain}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        if (!orderProducts.account_info) {
                          orderProducts.account_info = { domain: e.target.value };
                        } else orderProducts.account_info.domain = e.target.value;
                        setOrderProducts({ ...orderProducts });
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
              
              {/* And now to invite an admin */}
              <Card>
                <CardContent>
                  <Typography variant="h4">Admin Assignment</Typography>
                  <Typography variant="subtitle2">
                    After creating the Account, we will send a user invite to this chosen e-mail address.
                    This will be the first admin user on the Account, able to send additional user invites, view invoices, etc.
                  </Typography>
                  
                  <Stack
                    direction="row"
                    sx={{
                      gap: 2,
                      mt: 1,
                      alignItems: "end"
                    }}>
                    <Autocomplete openOnFocus autoComplete autoHighlight freeSolo style={{width: "75%", maxWidth: "30em" }}
                      disabled={isReadOnly}
                      options={hubspotDealInfo?.contacts || []}
                      getOptionLabel={(option: HubspotContact|string) =>
                        typeof option === 'string' ? option : `${option.name} (${option.email})`
                      }
                      // Value is set to a hubspot contact, if we have a match - otherwise just the string email
                      value={orderProducts.admin_email ? hubspotDealInfo?.contacts.find(c => c.email.toLowerCase() === orderProducts.admin_email?.toLowerCase()) || orderProducts.admin_email : null}
                      isOptionEqualToValue={(option: HubspotContact | string, value: HubspotContact | string) => {
                        const getComparison = (v: HubspotContact | string) => typeof v === 'string' ? v.toLowerCase() : v.email.toLowerCase();
                        return getComparison(option) === getComparison(value);
                      }}
                      renderInput={(params) => (
                        <TextField variant="standard" label="Admin user" error={!orderProducts.admin_email}
                          {...params}
                        />
                      )}
                      onChange={(_event, value: string|HubspotContact|null) => {
                        setOrderProducts({...orderProducts,
                          admin_email: (value && typeof value !== 'string') ? value.email : (value ? value : undefined)
                        })
                      }}
                    />
                    
                    <TextField select label="Role" size="small" disabled={isReadOnly}
                      value={orderProducts.admin_role}
                      onChange={(e) => setOrderProducts({ ...orderProducts, admin_role: e.target.value })}
                    >
                      {Object.entries(billingProducts.roles).map(([key, role]) => (
                        <MenuItem key={`admin-role-${key}`} value={key}>{role.name}</MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </CardContent>
              </Card>
            </>}
            
            {/* What to do with this order */}
            <Card>
              <CardContent>
                <Typography variant="h4">Order Destination</Typography>
                <Typography variant="subtitle2">
                  We can send it to the customer via e-mail, which will allow the customer to digitally &ldquo;sign&rdquo; the order. If we&apos;re not told to send it, you should send it yourself for customer approval.<br/>
                  You can also download a PDF of the Order, which you can send for signing, use with a service like DocuSign, etc., then the order would be manually approved to activate after.
                </Typography>
                
                <Stack
                  direction="row"
                  sx={{
                    gap: 2,
                    mt: 1,
                    alignItems: "end"
                  }}>
                  <FormGroup>
                    <FormControlLabel label={`Send by email ${orderProducts._send_order_by_email ? "enabled" : "disabled"}`}
                        control={
                          <Switch disabled={isReadOnly}
                            value={orderProducts._send_order_by_email} defaultChecked={orderProducts._send_order_by_email ? true : undefined}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                              if(checked) {
                                setOrderProducts({ ...orderProducts, _send_order_by_email: true });
                              } else {
                                setOrderProducts({ ...orderProducts, _send_order_by_email: false, order_email: undefined });
                              }
                            }}
                          />
                        }
                    />
                  </FormGroup>
                  {orderProducts._send_order_by_email &&
                    <Autocomplete openOnFocus autoComplete autoHighlight freeSolo style={{width: "75%", maxWidth: "30em" }}
                      disabled={isReadOnly}
                      options={hubspotDealInfo?.contacts || []}
                      getOptionLabel={(option: HubspotContact|string) =>
                        typeof option === 'string' ? option : `${option.name} (${option.email})`
                      }
                      // Value is set to a hubspot contact, if we have a match - otherwise just the string email
                      value={orderProducts.order_email ? hubspotDealInfo?.contacts.find(c => c.email.toLowerCase() === orderProducts.order_email?.toLowerCase()) || orderProducts.order_email : null}
                      isOptionEqualToValue={(option: HubspotContact | string, value: HubspotContact | string) => {
                        const getComparison = (v: HubspotContact | string) => typeof v === 'string' ? v.toLowerCase() : v.email.toLowerCase();
                        return getComparison(option) === getComparison(value);
                      }}
                      renderInput={(params) => (
                        <TextField variant="standard" label="Order email" error={!orderProducts.order_email}
                          {...params}
                        />
                      )}
                      onChange={(_event, value: string|HubspotContact|null) => {
                        setOrderProducts({
                          ...orderProducts,
                          order_email: (value && typeof value !== 'string') ? value.email : (value ? value : undefined)
                        });
                      }}
                    />
                  }
                </Stack>
              </CardContent>
            </Card>

            {/* Now we provide selections for rep assignments. Used for commissions calculations and notifications. */}
            <Card>
              <CardContent>
                <Typography variant="h4">Assigned Reps</Typography>
                <Typography variant="subtitle2" sx={{
                  mb: 1
                }}>
                  These assignments are used to calculate commissions or other such compensation, if warranted, 
                  as well as provide notification of Order-related events, such as Order completed, Invoice sent, Payment received, etc.
                  NOTE: Multiple users can be entered into a single slot. If a commision is to be calculated, it will be evenly split amongst those in the same slot.
                </Typography>

                <Stack direction="column" sx={{
                  gap: 1
                }}>
                  <RepSelection users={infegyUsers} label="Lead Source" disabled={isReadOnly && !isSuperAdmin}
                    value={orderProducts.reps?.['lead_source'] || null}
                    onChange={(users) => updateReps('lead_source', users)}
                  />
                  <RepSelection users={infegyUsers} label="Lead Chase" disabled={isReadOnly && !isSuperAdmin}
                    value={orderProducts.reps?.['lead_chase'] || null}
                    onChange={(users) => updateReps('lead_chase', users)}
                  />
                  <RepSelection users={infegyUsers} label="Account Executive (AE)" disabled={isReadOnly && !isSuperAdmin}
                    value={orderProducts.reps?.['account_exec'] || null}
                    onChange={(users) => updateReps('account_exec', users)}
                  />
                  <RepSelection users={infegyUsers} label="Solutions Consultant (SC/SE)" disabled={isReadOnly && !isSuperAdmin}
                    value={orderProducts.reps?.['solutions_consultant'] || null}
                    onChange={(users) => updateReps('solutions_consultant', users)}
                  />
                  <RepSelection users={infegyUsers} label="Client Success" disabled={isReadOnly && !isSuperAdmin}
                    value={orderProducts.reps?.['client_success'] || null}
                    onChange={(users) => updateReps('client_success', users)}
                  />
                </Stack>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent>
                <Stack sx={{
                  gap: 1
                }}>
                  {!orderProducts._trial && <>
                    <Typography variant="h4" sx={{
                      mb: 1
                    }}>Invoice Memo and Notes</Typography>
                    <Typography variant="subtitle2">
                      This is additional note text <em>that will appear on the order form</em>.
                    </Typography>
                    <TextField multiline fullWidth disabled={isReadOnly}
                      label="Order Form Note"
                      value={orderProducts.order_form_note || ''}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setOrderProducts({ ...orderProducts, order_form_note: e.target.value || undefined })
                      }
                    />
                  
                    {!orderProducts._signed_at && <>
                      <Typography variant="subtitle2" sx={{
                        mt: 1
                      }}>
                        This is additional memo text <em>that will appear on invoices</em>.
                      </Typography>
                      <TextField multiline fullWidth disabled={isReadOnly}
                        label="Invoice Memo"
                        value={orderProducts.invoice_memo || ''}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          setOrderProducts({ ...orderProducts, invoice_memo: e.target.value || undefined })
                        }
                      />
                    </>}
                  
                  </>}

                  <Typography variant="subtitle2" sx={{
                    mt: 1
                  }}>
                    These notes are for internal records about the deal. Setup requirements, project details, etc.
                  </Typography>
                  <TextField multiline fullWidth disabled={isReadOnly}
                    label="Internal Notes"
                    value={orderProducts.internal_notes || ''}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => 
                      setOrderProducts({...orderProducts, internal_notes: e.target.value || undefined})
                    }
                  />
                </Stack>
              </CardContent>
            </Card>
          </>
        )}
        
        {/* Interaction buttons on bottom, to delete (only when created and not signed), approve (when created and not approved), and create or update (when not approved)  */}
        <Stack
          sx={{
            width: "100%",
            alignItems: "end",
            gap: 2
          }}>
          {saveError &&
            <Typography color="error" variant="h5" sx={{
              mb: 1
            }}>{saveError}</Typography>
          }
          
          <Stack
            direction="row"
            sx={{
              width: "100%",
              justifyContent: orderId ? "space-between" : "end",
              gap: 2
            }}>
            {orderId && !orderProducts._signed_at && (!isReadOnly || isSuperAdmin) &&
              <Button variant="outlined" color="error" loading={saveState === 'saving'}
                onClick={() => deleteOrder()}
              >
                Delete
              </Button>
            }
            
            {orderId && !orderProducts._approved_at && !isReadOnly && isSuperAdmin &&
              <Button variant="outlined" color="warning" loading={saveState === 'saving'}
                disabled={isSaveEnabled()}
                onClick={() => approveOrder()}
              >
                Approve
              </Button>
            }
            {orderId && orderProducts._approved_at && !orderProducts._signed_at && isSuperAdmin &&
              <Button variant="outlined" color="warning"
                disabled={!!orderProducts._sent_at}
                loading={saveState === 'saving'}
                onClick={() => unapproveOrder()}
              >
                Un-approve
              </Button>
            }
            
            {orderId && orderProducts._approved_at && !orderProducts._signed_at && isSuperAdmin && <>
              <Button variant="outlined" color="success"
                loading={saveState === 'saving'}
                onClick={() => setSignDialogOpen(true)}
              >
                Override Signing
              </Button>
              
              <Dialog open={signDialogOpen} onClose={() => { if (signingInProgress) return; setSignDialogOpen(false); }} scroll="paper">
                <DialogTitle>Override Signing</DialogTitle>
                <DialogContent>
                  <DialogContentText sx={{
                    paddingBottom: 1
                  }}>
                    Manually mark this order form as customer-signed.<br/>
                    This considers the order signed and active!<br/>
                    This should ONLY be done if the customer confirmed the order externally, such as via DocuSign, Hubspot, or some other method.
                  </DialogContentText>
                  <form onSubmit={handleSigning} id="override-signing-form">
                    <TextField autoFocus fullWidth variant="filled" margin="dense" multiline rows={3} required
                       id="details" name="details"
                      label="Signing Details (please note anything relevant, like where, how?)"
                    />
                    <TextField fullWidth variant="standard" margin="dense" required
                       id="signed_by" name="signed_by"
                      label="Signed by (name)"
                    />
                    <TextField fullWidth variant="standard" margin="dense"
                      id="billing_emails" name="billing_emails"
                      label="Billing Emails (comma-separated)"
                    />
                    <TextField fullWidth variant="standard" margin="dense"
                       id="po_number" name="po_number"
                      label="PO Number (optional)"
                    />
                    <TextField fullWidth variant="filled" margin="dense" multiline rows={2}
                       id="billing_address" name="billing_address"
                      label="Billing Address (please provide if required for invoicing)"
                    />
                  </form>
                </DialogContent>
                {signingError &&
                  <DialogContentText color="error" sx={{
                    paddingLeft: 3
                  }}>
                    Signing error occurred!<br />
                    {signingError}
                  </DialogContentText>
                }
                <DialogActions>
                  <Button onClick={() => setSignDialogOpen(false)} disabled={signingInProgress}>Cancel</Button>
                  <Button type="submit" form="override-signing-form" loading={signingInProgress}>
                    Override Signing
                  </Button>
                </DialogActions>
              </Dialog>
            </>}
            
            {((!isReadOnly && !orderProducts._approved_at) || (isSuperAdmin && isSaveEnabled())) &&
              <Button variant="outlined" color="success" loading={saveState === 'saving'}
                disabled={!isSaveEnabled()}
                onClick={() => saveOrder()}
              >
                {orderId ? "Update" : "Create Order"}
              </Button>
            }
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
